import { DISPLAY_DATE_FORMAT } from 'constants/dates';
import { useMemo, useRef, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IconButton } from '@mui/material';
import { useAuth } from 'contexts/authContext';
import { format } from 'date-fns';
import { CorrectiveInvoice, Invoice } from 'pages/invoices/invoices.types';
import { useTranslation } from 'react-i18next';
import { FaList } from 'react-icons/fa';
import {
  canInvoiceBeUnpublished,
  canInvoiceBePublished,
  isForeignInvoice,
  isInvoiceCorrective,
  canPublishInvoice,
  canChangeInvoiceStatus,
  canPrintInvoice,
} from 'utils/invoiceFunctions.utils';
import * as D from '../../details.styled';
import { FetchInvoiceData } from '../../InvoiceDetails.types';
import { CorrectiveInvoiceLines } from './components/CorrectiveInvoiceLines';
import { InvoiceLines } from './components/InvoiceLines';
import { OptionsMenu } from './components/OptionsMenu';
import * as S from './InvoiceLinesPanel.css';

type Props = {
  invoice: Invoice | CorrectiveInvoice;
  refetchInvoiceData: FetchInvoiceData;
};

export const InvoiceLinesPanel = ({ invoice, refetchInvoiceData }: Props) => {
  const { t } = useTranslation(['invoice']);

  const { user, allowedReplacementsByCode } = useAuth();

  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const optionsRef = useRef(null);

  const handleOptionsOptionsOpen = (event: React.MouseEvent<any>) => {
    event.stopPropagation();
    setIsOptionsOpen((prev) => !prev);
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const publicationDate: string | null = useMemo(
    () =>
      (!!invoice.publicationDate &&
        format(invoice.publicationDate, DISPLAY_DATE_FORMAT)) ||
      null,
    [invoice],
  );

  const isCorrective = useMemo(
    () => isInvoiceCorrective(invoice.invoiceClass),
    [invoice],
  );

  return (
    <>
      <D.StyledAccordion defaultExpanded>
        <D.IconLeftAccordionSummary
          expandIcon={<ExpandMoreIcon fontSize='large' />}
        >
          <D.Subtitle>{t('invoice', { ns: 'invoice' })}</D.Subtitle>
          {(isCorrective || (!isCorrective && !invoice.deleted)) && (
            <IconButton
              size='small'
              color='inherit'
              ref={optionsRef}
              onClick={handleOptionsOptionsOpen}
            >
              <FaList size={18} />
            </IconButton>
          )}
        </D.IconLeftAccordionSummary>
        {!!publicationDate && (
          <S.PublicationDateContainer>
            <S.PublicationDateLabel>
              {t('publicationDate', { ns: 'invoice' })}:
            </S.PublicationDateLabel>
            {publicationDate}
          </S.PublicationDateContainer>
        )}
        {!isCorrective ? (
          <InvoiceLines
            invoice={invoice as Invoice}
            refetchInvoiceData={refetchInvoiceData}
          />
        ) : (
          <CorrectiveInvoiceLines
            invoice={invoice as CorrectiveInvoice}
            refetchInvoiceData={refetchInvoiceData}
          />
        )}
      </D.StyledAccordion>
      <OptionsMenu
        invoice={invoice}
        isOptionsOpen={isOptionsOpen}
        clickAwayHandler={() => setIsOptionsOpen(false)}
        anchorEl={optionsRef.current}
        isCorrective={isCorrective}
        isOwn={!isForeignInvoice(invoice.invoiceClass)}
        requiresCorrection={invoice.requiresCorrection}
        invoicePublished={!!invoice.publicationDate}
        isPaid={invoice.paid}
        canBeUnpublished={canInvoiceBeUnpublished(
          invoice,
          invoice.status,
          user,
          allowedReplacementsByCode,
        )}
        canBePublished={canInvoiceBePublished(invoice)}
        canPublish={canPublishInvoice(invoice, user, allowedReplacementsByCode)}
        canChangeStatus={canChangeInvoiceStatus(
          invoice,
          user,
          allowedReplacementsByCode,
        )}
        canPrint={canPrintInvoice(user)}
        refetchInvoiceData={refetchInvoiceData}
      />
    </>
  );
};
